// Message — the state-machine/lockstep command message. mpshared/mpht.h:138-233.
//
// THE UNION IS OVERLAPPING STORAGE, faithfully: the C++ writes one arm and sometimes reads
// another that aliases the same bytes (e.g. the SendMineCommand macro at game/ht.h:2106
// fills msg.MoveCommand, while TrackState reads msg.MineCommand — the arms share their
// layout prefix). A TS object-per-arm would silently break that, so the union is a
// Uint16Array(8) and each arm is a lazily-cached accessor view over the same words.
// Union dword fields (HuntEnemies.um) use C++ little-endian word order (low word first).

import { s16, u16 } from '../math/int32.ts';

// mpshared/mpht.h:235-273 (enumerated 0..37; order is load-bearing — gmpmidQuad indexes it)
export const kmidReservedNull = 0;
export const kmidReservedEnter = 1;
export const kmidReservedExit = 2;
export const kmidReservedUpdate = 3;
export const kmidHit = 4;
export const kmidNearbyAllyHit = 5;
export const kmidDelete = 6;
export const kmidEnemyNearby = 7;
export const kmidPowerLowHigh = 8;
export const kmidMoveWaitingNearby = 9;
export const kmidBeingUpgraded = 10;
export const kmidUpgradeComplete = 11;
export const kmidMoveCommand = 12;
export const kmidAttackCommand = 13;
export const kmidAnimationComplete = 14;
export const kmidBuildOtherCommand = 15;
export const kmidAbortBuildOtherCommand = 16;
export const kmidBuildComplete = 17;
export const kmidFireComplete = 18;
export const kmidSpawnSmoke = 19;
export const kmidSelfDestructCommand = 20;
export const kmidRepairCommand = 21;
export const kmidUpgradeCommand = 22;
export const kmidAbortUpgradeCommand = 23;
export const kmidTransformCommand = 24;
export const kmidDeliverCommand = 25;
export const kmidGalaxiteDelivery = 26;
export const kmidMineCommand = 27;
export const kmidMoveAction = 28;
export const kmidAttackAction = 29;
export const kmidGuardAction = 30;
export const kmidGuardVicinityAction = 31;
export const kmidGuardAreaAction = 32;
export const kmidHuntEnemiesAction = 33;
export const kmidFire = 34;
export const kmidHeal = 35;
export const kmidPlaySfx = 36;
export const kmidPlayerDisconnect = 37;
// Attack-move: a player-issued command (relayed through the lockstep CommandQueue) that
// drives a unit toward a destination while engaging enemies en route, then resumes — the
// move-action behavior exposed to player input. NOT in the original C++ enum (the shipped
// engine had no attack-move command); additive ordinal past kmidPlayerDisconnect, so old
// replays/saves (which never contain it) are unaffected. Reuses the MoveCommand payload.
export const kmidAttackMoveCommand = 38;

// game/ht.h:1960-1985 State enum (order load-bearing)
export const kstReservedNull = 0;
export const kstReservedZombie = 1;
export const kstReservedGlobal = 2;
export const kstGuard = 3;
export const kstMove = 4;
export const kstAttack = 5;
export const kstChase = 6;
export const kstIdle = 7;
export const kstDying = 8;
export const kstBuildOtherCompleting = 9;
export const kstBeingBuilt = 10;
export const kstHuntEnemies = 11;
export const kstProcessorGetMiner = 12;
export const kstProcessorPutMiner = 13;
export const kstProcessorTakeGalaxite = 14;
export const kstMinerMoveToProcessor = 15;
export const kstMinerRotateForEntry = 16;
export const kstMinerMine = 17;
export const kstMinerFindGalaxite = 18;
export const kstMinerFaceGalaxite = 19;
export const kstMinerApproachGalaxite = 20;
export const kstMinerSuck = 21;
export const kstMinerStepAside = 22;
export const kstChangeStatePendingFireComplete = 23;
export const kstContinueActionPendingFireComplete = 24;

// game/ht.h:2144-2146
export const knHandled = 1;
export const knNotHandled = 0;
export const knDeleted = -1;

// game/ht.h:2133-2136
export const kfUpgradeHrc = 0x0001;
export const kfUpgradeVts = 0x0002;
export const kfUpgradeHrcInProgress = 0x0004;
export const kfUpgradeVtsInProgress = 0x0008;

// Null ids: Gid/StateMachineId are words; null is (word)-1 = 0xffff — which carries the
// kwfGidEndMarker bit (0x8000), so GetGob(kgidNull) is caught by the marker check.
// game/ht.h:682 ksmidNull, game/ht.h:6137 kgidNull.
export const ksmidNull = 0xffff;
export const kgidNull = 0xffff;

export const MESSAGE_SIZE = 28; // wire bytes: 12 header + 16 union

interface WPointView {
  wx: number;
  wy: number;
}

/** One state-machine message. Header fields are plain; the union is `u` (8 words). */
export class Message {
  mid = 0; // word
  smidSender = 0; // word
  smidReceiver = 0; // word
  wDummy = 0; // word (alignment; present on the wire)
  tDelivery = 0; // int (sim ticks)
  u = new Uint16Array(8);

  // ---- arm views (lazily cached; all share `u`) -------------------------------
  private mkWpt(ix: number, iy: number): WPointView {
    const u = this.u;
    return {
      get wx() {
        return s16(u[ix]);
      },
      set wx(v: number) {
        u[ix] = u16(v);
      },
      get wy() {
        return s16(u[iy]);
      },
      set wy(v: number) {
        u[iy] = u16(v);
      },
    };
  }

  private _hit?: { gidAssailant: number; sideAssailant: number; nDamage: number };
  get Hit() {
    const u = this.u;
    return (this._hit ??= {
      get gidAssailant() {
        return u[0];
      },
      set gidAssailant(v: number) {
        u[0] = u16(v);
      },
      get sideAssailant() {
        return u[1];
      },
      set sideAssailant(v: number) {
        u[1] = u16(v);
      },
      get nDamage() {
        return s16(u[2]);
      },
      set nDamage(v: number) {
        u[2] = u16(v);
      },
    });
  }

  private _playSfx?: { sfx: number };
  get PlaySfx() {
    const u = this.u;
    return (this._playSfx ??= {
      get sfx() {
        return u[0];
      },
      set sfx(v: number) {
        u[0] = u16(v);
      },
    });
  }

  private _enemyNearby?: { gidEnemy: number };
  get EnemyNearby() {
    const u = this.u;
    return (this._enemyNearby ??= {
      get gidEnemy() {
        return u[0];
      },
      set gidEnemy(v: number) {
        u[0] = u16(v);
      },
    });
  }

  // MoveCommand / AttackCommand / (prefix of) MineCommand share one layout:
  // gidTarget u0, wptTarget u1/u2, wptTargetCenter u3/u4, tcTargetRadius u5,
  // wcMoveDistPerUpdate u6 (mpshared/mpht.h:166-188)
  private _move?: {
    gidTarget: number;
    wptTarget: WPointView;
    wptTargetCenter: WPointView;
    tcTargetRadius: number;
    wcMoveDistPerUpdate: number;
  };
  private mkMove() {
    const u = this.u;
    return {
      get gidTarget() {
        return u[0];
      },
      set gidTarget(v: number) {
        u[0] = u16(v);
      },
      wptTarget: this.mkWpt(1, 2),
      wptTargetCenter: this.mkWpt(3, 4),
      get tcTargetRadius() {
        return s16(u[5]);
      },
      set tcTargetRadius(v: number) {
        u[5] = u16(v);
      },
      get wcMoveDistPerUpdate() {
        return s16(u[6]);
      },
      set wcMoveDistPerUpdate(v: number) {
        u[6] = u16(v);
      },
    };
  }
  get MoveCommand() {
    return (this._move ??= this.mkMove());
  }
  get AttackCommand() {
    return (this._move ??= this.mkMove());
  }
  // Attack-move reuses the Move/Attack union layout (destination in wptTarget).
  get AttackMoveCommand() {
    return (this._move ??= this.mkMove());
  }

  private _upgrade?: { wfUpgrade: number };
  get UpgradeCommand() {
    const u = this.u;
    return (this._upgrade ??= {
      get wfUpgrade() {
        return u[0];
      },
      set wfUpgrade(v: number) {
        u[0] = u16(v);
      },
    });
  }

  private _build?: { ut: number; wpt: WPointView };
  get BuildOtherCommand() {
    const u = this.u;
    return (this._build ??= {
      get ut() {
        return u[0];
      },
      set ut(v: number) {
        u[0] = u16(v);
      }, // word ut
      wpt: this.mkWpt(1, 2),
    });
  }

  private _abortBuild?: { ut: number };
  get AbortBuildOtherCommand() {
    const u = this.u;
    return (this._abortBuild ??= {
      // UnitType is SIGNED short (mpshared/mpht.h) — contrast BuildOtherCommand's word ut
      get ut() {
        return s16(u[0]);
      },
      set ut(v: number) {
        u[0] = u16(v);
      },
    });
  }

  private _deliver?: { gidTarget: number };
  get DeliverCommand() {
    const u = this.u;
    return (this._deliver ??= {
      get gidTarget() {
        return u[0];
      },
      set gidTarget(v: number) {
        u[0] = u16(v);
      },
    });
  }

  private _mine?: { gidTarget: number; wptTarget: WPointView };
  get MineCommand() {
    const u = this.u;
    return (this._mine ??= {
      get gidTarget() {
        return u[0];
      },
      set gidTarget(v: number) {
        u[0] = u16(v);
      },
      wptTarget: this.mkWpt(1, 2),
    });
  }

  private _guardArea?: { nArea: number };
  get GuardAreaCommand() {
    const u = this.u;
    return (this._guardArea ??= {
      get nArea() {
        return u[0];
      },
      set nArea(v: number) {
        u[0] = u16(v);
      },
    });
  }

  private _hunt?: { um: number };
  get HuntEnemiesCommand() {
    const u = this.u;
    return (this._hunt ??= {
      // UnitMask is a dword: C++ little-endian union layout = low word first
      get um() {
        return (u[0] | (u[1] << 16)) >>> 0;
      },
      set um(v: number) {
        u[0] = v & 0xffff;
        u[1] = (v >>> 16) & 0xffff;
      },
    });
  }

  private _disconnect?: { pid: number; nReason: number };
  get PlayerDisconnect() {
    const u = this.u;
    return (this._disconnect ??= {
      get pid() {
        return u[0];
      },
      set pid(v: number) {
        u[0] = u16(v);
      },
      get nReason() {
        return u[1];
      },
      set nReason(v: number) {
        u[1] = u16(v);
      },
    });
  }
}

/** memset(&msg, 0, sizeof(msg)) + field assignment — the C++ construction idiom. */
export function newMessage(mid = 0, smidSender = 0, smidReceiver = 0): Message {
  const m = new Message();
  m.mid = mid;
  m.smidSender = smidSender;
  m.smidReceiver = smidReceiver;
  return m;
}

/** pdmsg->msg = *pmsg — full struct copy (never alias; aliasing desyncs). */
export function cloneMessage(src: Message): Message {
  const m = new Message();
  m.mid = src.mid;
  m.smidSender = src.smidSender;
  m.smidReceiver = src.smidReceiver;
  m.wDummy = src.wDummy;
  m.tDelivery = src.tDelivery;
  m.u.set(src.u);
  return m;
}

// ---- 28-byte wire codec ------------------------------------------------------------
// netmessage.cpp:19-127 MessageByteOrderSwap: the client swaps ONLY each command arm's
// meaningful fields (mid/smids/tDelivery + the per-arm list) — NEVER the header wDummy
// or the union pad words. The unswapped words keep their raw wire bytes in C++ MEMORY
// (stale sender-stack pads included), and Game::SaveGame serializes that memory — so
// this codec reads exactly the swapped positions big-endian and the rest as the
// little-endian memory image (verified byte-for-byte against the real client by
// auto/savegame-xray.mjs). Bit i set = union word u[i] is byte-swapped on the wire.
const gmpmidSwappedU: Record<number, number> = {
  [kmidMoveCommand]: 0x7f, // gid, wpt.x/y, wptCenter.x/y, tcRadius, wcMove
  [kmidAttackCommand]: 0x7f,
  [kmidAttackMoveCommand]: 0x7f, // same payload as MoveCommand (TS-only command)
  [kmidMineCommand]: 0x07, // gid, wpt.x/y
  [kmidBuildOtherCommand]: 0x07, // ut, wpt.x/y
  [kmidAbortBuildOtherCommand]: 0x01, // ut
  [kmidUpgradeCommand]: 0x01, // wfUpgrade
  [kmidDeliverCommand]: 0x01, // gidTarget
  [kmidTransformCommand]: 0x00,
  [kmidSelfDestructCommand]: 0x00,
  [kmidRepairCommand]: 0x00,
  [kmidAbortUpgradeCommand]: 0x00,
  [kmidPlayerDisconnect]: 0x03, // pid, nReason
};

export function decodeMessage(bytes: Uint8Array): Message {
  if (bytes.length < MESSAGE_SIZE) throw new Error(`message blob too short: ${bytes.length}`);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, MESSAGE_SIZE);
  const m = new Message();
  m.mid = dv.getUint16(0);
  m.smidSender = dv.getUint16(2);
  m.smidReceiver = dv.getUint16(4);
  m.wDummy = dv.getUint16(6, true); // never swapped (netmessage.cpp)
  m.tDelivery = dv.getInt32(8);
  // netmessage.cpp's default arm (unclassified mid) swaps NO union words — bug-compat
  const swapped = gmpmidSwappedU[m.mid] ?? 0x00;
  for (let i = 0; i < 8; i++) m.u[i] = dv.getUint16(12 + i * 2, (swapped & (1 << i)) === 0);
  return m;
}

// ---- 28-byte MEMORY-image codec (LITTLE-endian) -----------------------------------
// Game::SaveGame writes Message structs as raw wasm32 memory (pstm->Write(&m_msgPending,
// sizeof) — MobileUnit.cpp:365, StateMachine delayed messages, CommandQueue), which is
// LITTLE-endian — unlike the BIG-endian wire codec above (a 68K-era convention the
// client byte-swaps for on send/receive). Savegame serialization MUST use these
// (verified byte-for-byte vs the real client by auto/savegame-xray.mjs).

export function decodeMessageMem(bytes: Uint8Array): Message {
  if (bytes.length < MESSAGE_SIZE) throw new Error(`message blob too short: ${bytes.length}`);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, MESSAGE_SIZE);
  const m = new Message();
  m.mid = dv.getUint16(0, true);
  m.smidSender = dv.getUint16(2, true);
  m.smidReceiver = dv.getUint16(4, true);
  m.wDummy = dv.getUint16(6, true);
  m.tDelivery = dv.getInt32(8, true);
  for (let i = 0; i < 8; i++) m.u[i] = dv.getUint16(12 + i * 2, true);
  return m;
}

export function encodeMessageMem(m: Message): Uint8Array {
  const out = new Uint8Array(MESSAGE_SIZE);
  const dv = new DataView(out.buffer);
  dv.setUint16(0, m.mid, true);
  dv.setUint16(2, m.smidSender, true);
  dv.setUint16(4, m.smidReceiver, true);
  dv.setUint16(6, m.wDummy, true);
  dv.setInt32(8, m.tDelivery | 0, true);
  for (let i = 0; i < 8; i++) dv.setUint16(12 + i * 2, m.u[i], true);
  return out;
}

export function encodeMessage(m: Message): Uint8Array {
  const out = new Uint8Array(MESSAGE_SIZE);
  const dv = new DataView(out.buffer);
  dv.setUint16(0, m.mid);
  dv.setUint16(2, m.smidSender);
  dv.setUint16(4, m.smidReceiver);
  dv.setUint16(6, m.wDummy, true); // never swapped
  dv.setInt32(8, m.tDelivery | 0);
  const swapped = gmpmidSwappedU[m.mid] ?? 0x00; // default arm swaps no union words
  for (let i = 0; i < 8; i++) dv.setUint16(12 + i * 2, m.u[i], (swapped & (1 << i)) === 0);
  return out;
}
